import { DEFAULT_SCREEN_WIDTH, PAID_CHANNEL_POSITION_OFFSET } from 'common/config/constants';
import variables from 'common/config/variables';
import { SAFE_AREA_VIEW_PADDING } from 'common/config/variables/default';
import { CategoryGroups, LiveChannel } from 'common/constants/data-types';
import {
  AppTheme,
  ContainerOffsetMap,
  ContentScrollingContentAxis,
  RemoteKeyCodeMap,
  ScrollPositionAxisMap,
  StartOffsetPaddingMap
} from 'common/interfaces';
import { sort } from 'common/services/helpers';
import _, { isEmpty, isNil } from 'lodash';
import React from 'react';
import AnimationDurations from './AnimationDurations';
import { isInputFieldFocused } from './helpers';
import smoothScroll from './smoothScroll';

export function checkRequiredEnv() {
  const required = ['PRODUCT_NAME'];
  let required_index = 0;
  const envs = Object.keys(process.env);
  const found = required.every((r, index) => {
    required_index = index;
    return envs.indexOf(r) >= 0;
  });
  if (!found) {
    return `ENV variable '${required[required_index]} is not set'`;
  }
  return undefined;
}

export function groupByCategory<T = any>(
  data: ({ category: number; major_category: string; [key: string]: any } & T)[]
): CategoryGroups<T> {
  return _(data.sort((a, b) => a.category - b.category))
    .groupBy((e) => e.major_category)
    .map((value, key) => ({ title: key, items: value }))
    .value();
}

/**
 * Helper function to calculate a scalable pixel
 * so the non-relative unit (px) can scale and look the same on different resoltions.
 * Keep in mind this only works on screen with 16:9 aspect ratio
 * @param px
 * @returns
 */
// reading innerWidth forces a layout on old engines
let screenWidth = 0;
window.addEventListener('resize', () => {
  screenWidth = 0;
});
const getScreenWidth = () => screenWidth || (screenWidth = window.innerWidth);

export function getScalablePixel(px: number, invert = false) {
  if (invert) {
    return Math.round((DEFAULT_SCREEN_WIDTH / getScreenWidth()) * px);
  }
  return Math.round((getScreenWidth() / DEFAULT_SCREEN_WIDTH) * px);
}

export const offsetArray = (arr: Array<any>, offset: number) => [
  ...arr.slice(offset),
  ...arr.slice(0, offset)
];

/**
 * Helper function for generating keys for focusKey.
 * This key can help with focus managements
 * @param name
 * @param id
 * @returns
 */
export function getKey(name: string, id?: number | string, path?: string) {
  const _path = (path || location.pathname).replace(/\//, '');
  return `${!isNil(_path) ? _path + '-' : ''}${name}${!isNil(id) ? `-${id}` : ''}`;
}

export const getStripeEndPadding = () =>
  // variables.SAFE_AREA_VIEW_PADDING - variables.STRIPE_ITEM_PADDING;
  '50%';

export const getScrollOffset = (stripeItemWidth: number) =>
  -(
    stripeItemWidth +
    variables.STRIPE_ITEM_PADDING * 2 +
    // Calculate diff between default padding and current padding
    (variables.SAFE_AREA_VIEW_PADDING - SAFE_AREA_VIEW_PADDING) * 2
  ) / 2;

export const getDefaultSmoothScrollOptions = (
  stripeItemWidth: number = variables.STRIPE_ITEM_WIDTH
) => ({
  block: 'start',
  inline: 'center',
  allowAnimationOverlap: false,
  // Transition easing function:
  // easeOutQuart
  // cubic-bezier(0.25, 1, 0.5, 1)
  easing: (x: number) => 1 - Math.pow(1 - x, 4),
  //@ts-ignore
  duration: AnimationDurations.get('SCROLL_ANIMATION_DURATION'),
  paddingLeft: getScrollOffset(stripeItemWidth)
  // Removed with RTV-1033
  // paddingTop: -variables.STRIPE_ITEM_PADDING
});

// Tizen 3 (Chrome 47) has no KeyboardEvent.key
export const getKeyDigit = (e: KeyboardEvent): string | undefined => {
  const code = e.keyCode;
  if (code >= 48 && code <= 57 && !e.shiftKey) {
    return String(code - 48);
  }
  if (code >= 96 && code <= 105) {
    return String(code - 96);
  }
  return undefined;
};

export const getRemoteKeyName = (code: number) => {
  try {
    const codeName = RemoteKeyCodeMap[code];
    if (codeName === 'BACK' && isInputFieldFocused()) {
      return null;
    }
    return codeName;
  } catch (error) {
    console.warn('Could not find key name', error);
    return null;
  }
};

export const closeCurrentApp = () => {
  try {
    // Check if tizen app
    //@ts-ignore
    if (typeof window.tizen !== 'undefined') {
      //@ts-ignore
      window.tizen.application.getCurrentApplication().exit();
    } else if (navigator.userAgent.includes('webos')) {
      //@ts-ignore
      webOS.platformBack();
      window.close();
    } else {
      window.close();
      //@ts-ignore
      window.ReactNativeWebView.postMessage('CLOSE_APP');
    }
  } catch (error) {
    console.warn(error);
  }
};

export const screenSaverToggle = (state: boolean) => {
  try {
    //@ts-ignore
    if (typeof window.tizen !== 'undefined' && window.webapis) {
      //@ts-ignore
      webapis.appcommon.setScreenSaver(
        state
          ? //@ts-ignore
            webapis.appcommon.AppCommonScreenSaverState.SCREEN_SAVER_ON
          : //@ts-ignore
            webapis.appcommon.AppCommonScreenSaverState.SCREEN_SAVER_OFF,
        () => ({}),
        (e: any) => {
          throw new Error(e);
        }
      );
    }
  } catch (error) {
    console.warn(error);
  }
};

export function getClassName(
  className: string,
  props: { [s: string]: unknown } | ArrayLike<unknown>
) {
  let result = className;
  for (const key in props) {
    if (props[key]) {
      result += ' ' + key;
    }
  }
  return result;
}

export function getMouseMoveScrollHandler(
  scrollContainer: HTMLElement | null,
  axis: ContentScrollingContentAxis,
  size?: { x: number; y: number }
) {
  return (e: React.MouseEvent<HTMLElement>) => {
    // Enable mouse scroll only on horizontal stripes
    //@ts-ignore
    if (!scrollContainer || smoothScroll.scrolling()) {
      return;
    }
    let inline = 'center';

    const defaultOffset = variables.STRIPE_ITEM_WIDTH;
    const OffsetMap = size || { x: defaultOffset, y: defaultOffset };
    const PositionMap = { x: 'clientX', y: 'clientY' };
    const ScrollPositionMap = { x: 'xPos', y: 'yPos' };

    const containerRect = (scrollContainer as HTMLDivElement).getBoundingClientRect();
    const pointerPosition = e[PositionMap[axis]] - containerRect[StartOffsetPaddingMap[axis]];
    const containerOffset = containerRect[ContainerOffsetMap[axis]];
    if (pointerPosition < variables.SAFE_AREA_VIEW_PADDING) {
      inline = 'start';
    } else if (pointerPosition > containerOffset - variables.SAFE_AREA_VIEW_PADDING) {
      inline = 'end';
    } else {
      return;
    }
    const position =
      scrollContainer[ScrollPositionAxisMap[axis]] +
      OffsetMap[axis] * (inline === 'start' ? -1 : 1);
    //@ts-ignore
    smoothScroll({
      inline,
      [ScrollPositionMap[axis]]: position,
      scrollingElement: scrollContainer,
      allowAnimationOverlap: false,
      duration: AnimationDurations.get('SCROLL_ANIMATION_DURATION'),
      paddingLeft: 0
    });
  };
}
// Live channel array sorted as like it would be without the paid channel position mask
export const sortByPosition = (channels: LiveChannel[]) => {
  const items = channels.map((e) => ({
    ...e,
    access: e.access ? e.position : e.position * PAID_CHANNEL_POSITION_OFFSET
  }));

  return sort(items, false, variables.WHITELABEL_TYPE === AppTheme.LINEAR) as LiveChannel[];
};

export function interpolate(string: string, argumentArray: any[]) {
  const regex = /%s/;
  const _r = function (p: string, c: any) {
    return p.replace(regex, c);
  };
  return argumentArray.reduce(_r, string);
}

export function getCaretPosition(ctrl: HTMLInputElement | HTMLTextAreaElement) {
  let CaretPos = 0;
  const oldType = ctrl.type;
  ctrl.setAttribute('type', 'text');
  if (ctrl.selectionStart || ctrl.selectionStart == 0) {
    // Standard.
    CaretPos = ctrl.selectionStart;
  } else if (document['selection']) {
    // Legacy IE
    ctrl.focus();
    const range = document['selection'].createRange();
    range.moveStart('character', -ctrl.value.length);
    CaretPos = range.text.length;
  }
  ctrl.setAttribute('type', oldType);

  return CaretPos;
}

export function setCaretPosition(ctrl: HTMLInputElement | HTMLTextAreaElement, pos: number) {
  if (ctrl.setSelectionRange) {
    ctrl.focus();
    const oldType = ctrl.type;
    ctrl.setAttribute('type', 'text');
    ctrl.setSelectionRange(pos, pos);
    ctrl.setAttribute('type', oldType);
  }
}

export function getDeviceType() {
  //@ts-ignore
  if (typeof window.tizen !== 'undefined' || navigator.userAgent.match(/Tizen/i)) {
    return 'tizen';
  } else if (/web[o0]s/i.test(navigator.userAgent)) {
    return 'webos';
  } else if (navigator.userAgent.match(/Philips/i)) {
    return 'titanos';
    // @ts-ignore
  } else if (navigator.userAgent.match(/Hisense/i) || typeof Hisense_GetDeviceID === 'function') {
    return 'vidaa';
  }

  return 'web';
}

export function waitFor(callback: () => boolean, timeout = 1000) {
  const interval = setInterval(() => {
    const status = callback();
    if (status === true) {
      clearInterval(interval);
    }
  });
  setTimeout(() => {
    clearInterval(interval);
  }, timeout);
}

export function isTextOverflowing(htmlElement: HTMLElement, text: string, lines: number): boolean {
  htmlElement.style.webkitLineClamp = 'none';
  htmlElement.innerHTML = '<br />'.repeat(lines);
  const doubleLineHeight = htmlElement.offsetHeight;
  htmlElement.innerText = text;
  const realHeight = htmlElement.offsetHeight;
  htmlElement.style.webkitLineClamp = '';
  return realHeight >= doubleLineHeight;
}

/**
 * Truncates a given text to fit within a specified number of lines in an HTML element,
 * appending an ellipsis ("...") if the text exceeds the allowed lines.
 *
 * This function temporarily adjusts the `webkitLineClamp` style of the provided HTML element
 * to determine how much text fits within the specified number of lines. If the text overflows,
 * it truncates the text and appends an ellipsis.
 *
 * @param htmlElement - The target HTML element whose line clamp and height are used to measure text fitting.
 * @param text - The string to be truncated and displayed with ellipsis if necessary.
 * @param lines - The maximum number of lines the text should occupy.
 * @returns The possibly truncated string with an ellipsis if it exceeds the specified number of lines.
 */
export function formatStringToEllipsis(
  htmlElement: HTMLElement,
  text: string,
  lines: number
): string {
  htmlElement.style.webkitLineClamp = `${lines}`;
  htmlElement.innerText = text;
  const originalHeight = htmlElement.offsetHeight;

  const cleanUp = () => {
    htmlElement.style.webkitLineClamp = '';
  };
  if (lines <= 1) {
    cleanUp();
    return text;
  }

  // Temporarily set line clamp to one more than the original to measure overflow
  htmlElement.style.webkitLineClamp = `${lines + 1}`;
  const words = text.split(' ') || [];
  let index = 0;
  for (let i = 1; i <= words.length; i++) {
    htmlElement.textContent = words.slice(0, i).join(' ');
    if (htmlElement.offsetHeight > originalHeight) {
      htmlElement.textContent = text.slice(0, index - 3) + '...';
      cleanUp();
      return htmlElement.textContent;
    }
    index = htmlElement.textContent.length;
  }
  cleanUp();
  htmlElement.textContent = text;
  return text;
}

/**
 * Animate a numeric value to a target value over a specified duration.
 *
 * @param value - The starting numeric value.
 * @param end - The target numeric value to animate to.
 * @param duration - The duration of the animation in milliseconds.
 * @param animation - An optional easing function that takes a progress value (0 to 1) and returns an eased value.
 * @param onComplete - An optional callback function to be called when the animation completes.
 */
export function animateValue(
  value: number,
  end: number,
  duration: number,
  onUpdate: (value: number) => void,
  onComplete?: () => void,
  animation?: (progress: number) => number
) {
  const startTime = performance.now();
  const change = end - value;
  const start = value;

  function step(currentTime: number) {
    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1); // Clamp between 0 and 1

    // Linear easing (can replace with easing functions)
    value = animation ? animation(progress) : start + change * progress;
    onUpdate(value);

    if (progress < 1) {
      requestAnimationFrame(step);
    } else if (onComplete) {
      onComplete();
    }
  }

  requestAnimationFrame(step);
}

export function getSupportedVideoCodecs() {
  const codecs = [
    'video/mp4; codecs="avc1.42E01E"', // H.264
    'video/mp4; codecs="hev1.1.6.L93.B0"', // HEVC/H.265,
    'video/mp4; codecs="hvc1.2.4.L153.B0"', // HEVC/H.265 alternative,
    'video/mp4; codecs="hvc1.1.4.L126.B0"', // HEVC/H.265 alternative,
    'video/webm; codecs="vp8"',
    'video/webm; codecs="vp9"',
    'video/mp4; codecs="av01.0.05M.08"', // AV1
    'video/mp4; codecs="mp4v.20.8"' // MPEG-4
  ];

  return codecs.filter((codec) => MediaSource.isTypeSupported(codec));
}

let supportedVideoCodecs = '';
export function getSupportedVideoCodecNames() {
  const codecToName: Record<string, string> = {
    'video/mp4; codecs="avc1.42E01E"': 'h264',
    'video/mp4; codecs="hev1.1.6.L93.B0"': 'h265',
    'video/mp4; codecs="hvc1.2.4.L153.B0"': 'h265', // HEVC/H.265 alternative,
    'video/mp4; codecs="hvc1.1.4.L126.B0"': 'h265', // HEVC/H.265 alternative,
    'video/webm; codecs="vp8"': 'vp8',
    'video/webm; codecs="vp9"': 'vp9',
    'video/mp4; codecs="av01.0.05M.08"': 'av1',
    'video/mp4; codecs="mp4v.20.8"': 'mpeg-4'
  };
  if (isEmpty(supportedVideoCodecs)) {
    supportedVideoCodecs = getSupportedVideoCodecs()
      .map((codec) => codecToName[codec] || codec)
      .join(',');
  }
  return supportedVideoCodecs;
}
